import { useEffect, useState } from "react";

type Version = { buildId?: string; builtAt?: string; commit?: string };

/**
 * Shows the currently deployed build SHA + timestamp.
 * Reads /version.json emitted by the build-canary Vite plugin.
 *
 * variant="footer" — muted single-line for site footers.
 * variant="admin"  — larger card for admin surfaces.
 */
export function BuildInfo({
  variant = "footer",
  className = "",
}: {
  variant?: "footer" | "admin";
  className?: string;
}) {
  const [v, setV] = useState<Version | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/version.json?_=${Date.now()}`, { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((j) => !cancelled && setV(j))
      .catch((e) => !cancelled && setErr(e.message));
    return () => {
      cancelled = true;
    };
  }, []);

  const commit = v?.commit ?? (v?.buildId ? v.buildId.split("-").pop() : null);
  const builtAt = v?.builtAt ? new Date(v.builtAt) : null;
  const relative = builtAt ? formatRelative(builtAt) : null;

  if (variant === "admin") {
    return (
      <div
        className={`rounded-lg border border-border bg-card px-4 py-3 text-sm ${className}`}
        aria-label="Deployed build info"
      >
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">
          Deployed build
        </div>
        {err && <div className="text-destructive">version.json unavailable — {err}</div>}
        {v && (
          <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <code className="font-mono text-base text-foreground">{commit ?? "unknown"}</code>
            {builtAt && (
              <span className="text-muted-foreground">
                {builtAt.toISOString().replace("T", " ").slice(0, 19)} UTC
                {relative ? ` · ${relative}` : ""}
              </span>
            )}
          </div>
        )}
      </div>
    );
  }

  return (
    <p
      className={`text-[10px] font-mono tracking-wide text-primary-foreground/60 ${className}`}
      aria-label="Deployed build info"
      title={v?.buildId ?? undefined}
    >
      {err
        ? "build: unknown"
        : v
        ? `build ${commit ?? "?"}${builtAt ? ` · ${builtAt.toISOString().slice(0, 16).replace("T", " ")}Z` : ""}`
        : "build …"}
    </p>
  );
}

function formatRelative(d: Date): string {
  const diff = Date.now() - d.getTime();
  const m = Math.round(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export default BuildInfo;
